import {Controller} from "stimulus";

/**
 * CommentEditorController manages the data dictionary workspace.
 *
 * Architecture:
 * - A hidden master form holds all table/column comment values as hidden inputs.
 *   Each has `data-editor-scope` (e.g. "table_comment", "column_comments[3]")
 *   and `data-editor-field` (e.g. "description", "visibility").
 * - Two shared modals (table and column) contain a Turbo Frame in their body.
 *   Clicking Edit fetches server-rendered form fields into the frame, then
 *   copies master form values over them (to restore any unsaved edits).
 * - Edit buttons specify `data-editor-scope`, `data-editor-modal`,
 *   `data-editor-frame`, and `data-editor-frame-src`.
 *
 * On open: loads the Turbo Frame, copies master form values → modal inputs
 *          (matched by data-editor-field), then opens the modal.
 * On save: validates required fields, copies modal → master form, marks dirty,
 *          updates previews, and closes the modal.
 *
 * Adding a new tag requires only HTML changes (hidden input + modal field).
 * Zero JS changes.
 */
export default class CommentEditorController extends Controller {
  static targets = ["masterForm"];
  declare readonly masterFormTarget: HTMLFormElement;

  private _activeScope: string | null = null;
  private _activeModal: HTMLElement | null = null;

  // ----------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------

  openModal(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const scope = button.dataset.editorScope;
    const modalId = button.dataset.editorModal;
    const frameId = button.dataset.editorFrame;
    const frameSrc = button.dataset.editorFrameSrc;
    if (!scope || !modalId) return;

    this._activeScope = scope;
    const modal = document.getElementById(modalId);
    if (!modal) return;
    this._activeModal = modal;

    // Update modal title if the button specifies one
    const title = button.dataset.editorTitle;
    if (title) {
      const titleEl = modal.querySelector<HTMLElement>("[data-editor-title]");
      if (titleEl) titleEl.textContent = title;
    }

    // Load modal body via Turbo Frame, then populate and open
    if (frameId && frameSrc) {
      const frame = document.getElementById(frameId);
      if (frame) {
        frame.addEventListener(
          "turbo:frame-load",
          () => this.populateAndOpenModal(modal, scope),
          {once: true},
        );
        frame.setAttribute("src", frameSrc);
        return;
      }
    }

    // Fallback: open directly if no frame is configured
    this.populateAndOpenModal(modal, scope);
  }

  saveModal(_event: Event) {
    const scope = this._activeScope;
    const modal = this._activeModal;
    if (!scope || !modal) return;

    // Validate required fields
    if (!this.validateRequiredFields(modal)) return;

    // Copy modal → master form
    this.forEachModalField(modal, (modalEl, field) => {
      const masterEl = this.masterFormField(scope, field);
      if (masterEl) this.transferValue(modalEl, masterEl);
    });

    // Mark row as dirty
    const dirtyInput = this.masterFormField(
      scope,
      "_dirty",
    ) as HTMLInputElement | null;
    if (dirtyInput) dirtyInput.value = "1";

    // Update read-only previews
    this.element
      .querySelectorAll<HTMLElement>(
        `[data-editor-scope="${scope}"][data-editor-preview]`,
      )
      .forEach((preview) => {
        const field = preview.dataset.editorPreview!;
        const masterEl = this.masterFormField(scope, field);
        if (!masterEl) return;
        const value = this.readValue(masterEl);
        preview.textContent =
          field === "description" ? this.firstLine(value) : value || "\u2014";
      });

    // Show unsaved changes indicator
    this.element
      .querySelectorAll<HTMLElement>(
        `[data-editor-scope="${scope}"][data-editor-badge]`,
      )
      .forEach((badge) => {
        badge.classList.remove("hidden");
        const wrapper = badge.parentElement;
        const button = wrapper?.querySelector("button");
        if (button) button.classList.add("!bg-butterscotch-100", "rounded-lg");
        // Update the tooltip text (inside BoxContentComponent's rendered div)
        const tooltipContent = wrapper?.querySelector<HTMLElement>(
          "[data-admin--with-tooltip-target=\"tooltip\"] .text-sm",
        );
        if (tooltipContent) tooltipContent.textContent = badge.title;
      });

    // Close the specific active modal directly. We avoid dispatching
    // `tlw-modal:trigger-close` on document because that is a broadcast —
    // the shared modal controller's handleClose closes ALL modal instances,
    // not just the one we want. This caused the column modal to flash and
    // disappear immediately after opening when triggered shortly after saving
    // the table modal.
    this.closeModal(this._activeModal);
    this._activeScope = null;
    this._activeModal = null;
  }

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------

  /** Copies master form values into the modal, clears errors, and opens. */
  private populateAndOpenModal(modal: HTMLElement, scope: string) {
    // Copy master form → modal (matched by data-editor-field)
    this.forEachModalField(modal, (modalEl, field) => {
      const masterEl = this.masterFormField(scope, field);
      if (masterEl) this.transferValue(masterEl, modalEl);
    });

    // Clear previous validation errors
    modal
      .querySelectorAll<HTMLElement>("[data-editor-validation-for]")
      .forEach((el) => {
        el.textContent = "";
      });

    // Open the modal via its controller's public event API
    document.dispatchEvent(
      new CustomEvent("tlw-modal:trigger-open", {
        bubbles: false,
        detail: {modal: modal.id},
      }),
    );

    // Auto-size textareas after modal is visible
    requestAnimationFrame(() => this.autoSizeTextareas(modal));
  }

  /** Finds a master-form field by scope and field name. */
  private masterFormField(scope: string, field: string): HTMLElement | null {
    return this.masterFormTarget.querySelector<HTMLElement>(
      `[data-editor-scope="${scope}"][data-editor-field="${field}"]`,
    );
  }

  /** Iterates over all `[data-editor-field]` elements in the modal. */
  private forEachModalField(
    modal: HTMLElement,
    fn: (el: HTMLElement, field: string) => void,
  ) {
    modal.querySelectorAll<HTMLElement>("[data-editor-field]").forEach((el) => {
      fn(el, el.dataset.editorField!);
    });
  }

  /**
   * Reads the value from a form element, handling different element types:
   * - Checkbox: returns "1" or "0"
   * - Div (Marksmith wrapper): reads the inner textarea
   * - Everything else (input, textarea, select): .value
   */
  private readValue(el: HTMLElement): string {
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      return el.checked ? "1" : "0";
    }
    if (el.tagName === "DIV") {
      return el.querySelector<HTMLTextAreaElement>("textarea")?.value ?? "";
    }
    return (
      (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)
        .value ?? ""
    );
  }

  /**
   * Writes a value to a form element, handling different element types:
   * - Checkbox: sets .checked
   * - Div (Marksmith wrapper): sets the inner textarea and dispatches input
   * - Everything else: sets .value
   */
  private writeValue(el: HTMLElement, value: string) {
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      el.checked = value === "1";
      return;
    }
    if (el.tagName === "DIV") {
      const textarea = el.querySelector<HTMLTextAreaElement>("textarea");
      if (textarea) {
        textarea.value = value;
        textarea.dispatchEvent(new Event("input", {bubbles: true}));
      }
      return;
    }
    (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value =
      value;
  }

  /** Copies the value from one element to another. */
  private transferValue(from: HTMLElement, to: HTMLElement) {
    this.writeValue(to, this.readValue(from));
  }

  /**
   * Validates all modal fields marked with `data-editor-required`.
   * Shows errors via matching `[data-editor-validation-for]` elements.
   * Returns true if all pass, false if any fail.
   */
  private validateRequiredFields(modal: HTMLElement): boolean {
    let valid = true;

    modal
      .querySelectorAll<HTMLElement>("[data-editor-validation-for]")
      .forEach((el) => {
        el.textContent = "";
      });

    modal
      .querySelectorAll<HTMLElement>(
        "[data-editor-field][data-editor-required]",
      )
      .forEach((el) => {
        const value = this.readValue(el);
        if (value.trim()) return;

        const field = el.dataset.editorField!;
        const errorEl = modal.querySelector<HTMLElement>(
          `[data-editor-validation-for="${field}"]`,
        );
        if (errorEl) {
          errorEl.textContent =
            errorEl.dataset.editorValidationMessage ?? "can't be blank";

          // One-shot listener to clear error on user interaction
          const eventName =
            el instanceof HTMLSelectElement ? "change" : "input";
          const handler = () => {
            errorEl.textContent = "";
            el.removeEventListener(eventName, handler);
          };
          el.addEventListener(eventName, handler);
        }

        valid = false;
      });

    return valid;
  }

  /**
   * Closes a modal by directly manipulating its DOM state. We avoid
   * dispatching `tlw-modal:trigger-close` on document because that event is a
   * broadcast — the shared modal controller's handleClose closes ALL modal
   * instances, not just the targeted one.
   */
  private closeModal(modal: HTMLElement | null) {
    if (!modal) return;
    modal.classList.add("hidden");
    modal.removeAttribute("aria-modal");
  }

  /** Auto-sizes all resizable textareas in a container to fit their content. */
  private autoSizeTextareas(container: HTMLElement) {
    container
      .querySelectorAll<HTMLTextAreaElement>("textarea.resize-y")
      .forEach((textarea) => {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      });
  }

  /**
   * Returns the first non-blank line of a string, truncated to 80 chars.
   */
  private firstLine(text: string): string {
    const line = (text ?? "").split("\n").find((l) => l.trim()) ?? "";
    return line.length > 80 ? line.slice(0, 77) + "\u2026" : line;
  }
}
